/**
 * Utility functions for handling form data, especially null values from backend
 */

/**
 * Converts null values to undefined in an object
 * This is useful when initializing forms with data from backend that may contain null values
 *
 * @param data - The data object that may contain null values
 * @returns A new object with null values converted to undefined
 *
 * @example
 * const cleaned = cleanNullValues({ name: "John", email: null, phone: "123" });
 * // Returns: { name: "John", email: undefined, phone: "123" }
 */
export const cleanNullValues = <T extends Record<string, any>>(
  data: T | null | undefined
): Partial<T> => {
  if (!data) return {};

  return Object.fromEntries(
    Object.entries(data).map(([key, value]) => [
      key,
      value === null ? undefined : value,
    ])
  ) as Partial<T>;
};

/**
 * Cleans form submission data by converting null/empty values to undefined and removing undefined fields
 * This ensures GraphQL mutations don't receive null or empty string values for optional fields
 *
 * @param data - The form data that may contain null or empty values
 * @returns A new object with null/empty values removed
 *
 * @example
 * const cleaned = cleanFormData({ name: "John", email: null, phone: "", tagIds: [] });
 * // Returns: { name: "John", tagIds: [] }
 */
export const cleanFormData = <T extends Record<string, any>>(
  data: T
): Partial<T> => {
  return Object.fromEntries(
    Object.entries(data)
      .filter(([_, value]) => {
        // Keep boolean values (including false)
        if (typeof value === "boolean") return true;
        // Keep arrays (including empty arrays for tagIds, etc.)
        if (Array.isArray(value)) return true;
        // Keep numbers (including 0)
        if (typeof value === "number") return true;
        // Remove null, undefined, and empty strings
        return value !== null && value !== undefined && value !== "";
      })
      .map(([key, value]) => [key, value])
  ) as Partial<T>;
};

/**
 * Merges default values with initial data, cleaning null values
 * Useful for form initialization with backend data
 *
 * @param defaults - Default values for the form
 * @param initialData - Initial data from backend (may contain null)
 * @returns Merged object with null values cleaned
 *
 * @example
 * const values = mergeFormDefaults(
 *   { name: "", email: "" },
 *   { name: "John", email: null }
 * );
 * // Returns: { name: "John", email: undefined }
 */
export const mergeFormDefaults = <T extends Record<string, any>>(
  defaults: Partial<T>,
  initialData?: Partial<T> | null
): Partial<T> => {
  if (!initialData) return defaults;

  const cleaned = cleanNullValues(initialData);
  return { ...defaults, ...cleaned };
};

/**
 * Compares current form data with initial data and returns only changed fields
 * Useful for update operations to send only modified data
 *
 * @param currentData - Current form data
 * @param initialData - Initial data from backend
 * @returns Object containing only the fields that have changed
 *
 * @example
 * const changed = getChangedFields(
 *   { name: "John", age: 25 },
 *   { name: "John", age: 30 }
 * );
 * // Returns: { age: 25 }
 */
export const getChangedFields = <T extends Record<string, any>>(
  currentData: Partial<T>,
  initialData?: Partial<T> | null
): Partial<T> => {
  if (!initialData) return currentData;

  const changed: Partial<T> = {};

  // Check all fields in currentData
  for (const key in currentData) {
    const currentValue = currentData[key];
    const initialValue = initialData[key];

    // Handle arrays (like tagIds) — order matters, so compare without sorting
    if (Array.isArray(currentValue) && Array.isArray(initialValue)) {
      if (
        currentValue.length !== initialValue.length ||
        currentValue.some((val: any, i: number) => val !== initialValue[i])
      ) {
        changed[key] = currentValue;
      }
    }
    // Handle null/undefined comparisons
    else if (currentValue === null && initialValue === null) {
      // Both are null, no change
      continue;
    } else if (currentValue === undefined && initialValue === undefined) {
      // Both are undefined, no change
      continue;
    } else if (currentValue === null && initialValue === undefined) {
      // null vs undefined, consider as change
      changed[key] = currentValue;
    } else if (currentValue === undefined && initialValue === null) {
      // undefined vs null, consider as change
      changed[key] = currentValue;
    }
    // Handle primitive values
    else if (currentValue !== initialValue) {
      // Only include if the value has actually changed
      changed[key] = currentValue;
    }
  }

  return changed;
};
